import ContactNoteEntity from "../Entities/ContactNoteEntity";
import ApiError from "../network/ApiError";
import Network from "../network/network";
import Logger, { Loglevel } from "../util/Logger";
import Odata from "../util/Odata";
import BaseService from "./BaseService";

export default class AkquiseService extends BaseService<ContactNoteEntity> {
    private SERVICE_NAME = "AkquiseService";
    public static ODATA_ROUTE = "ContactNotes";

    protected getServiceName(): string {
        return this.SERVICE_NAME;
    }

    protected getOdataRoute(): string {
        return AkquiseService.ODATA_ROUTE;
    }

    protected mapDtoToEntity(dto: any): ContactNoteEntity {
        return ContactNoteEntity.fromOdata(dto, new ContactNoteEntity());
    }

    constructor(network: Network, logger: Logger) {
        super(network, logger);
    }

    public save = async (contactNote: ContactNoteEntity): Promise<ContactNoteEntity> => {
        try {
            let method = "POST";
            let route = this.getOdataRoute();
            if (contactNote.Id && contactNote.Id > 0) {
                route += `(${contactNote.Id})`;
                method = "PATCH";
            }
            const saved = await this.network.sendApi(route, ContactNoteEntity.toOdataObject(contactNote), method);
            return this.mapDtoToEntity(saved);
        } catch (e) {
            this.logger.log(Loglevel.Error, "Unable to save contactnote", e);
        }
    };

    public linkSynchronizedAccount = async (contactNoteId: number, account: string, itemId: string): Promise<void> => {
        try {
            await this.network.sendApi(`${this.getOdataRoute()}/CreateSyncSourceReference(Id=${contactNoteId},Account=${Odata.stringLiteral(account)},ItemId=${Odata.stringLiteral(itemId)})`);
        } catch (e) {
            // A mail the user never synchronized has nothing to link, which is the everyday outcome here.
            // Anything the endpoint did not answer that way - a refusal, a failure, an unreachable host -
            // is not, and used to be swallowed together with it.
            const hasNothingToLink = ApiError.isApiError(e) && (e.status === 400 || e.status === 404);
            const level = hasNothingToLink ? Loglevel.Info : Loglevel.Error;
            this.logger.log(level, "Could not link the synchronized account to the contact note", e);
        }
    };

    public linkPersonToInstitutionEntry = async (contactNoteId: number, personId: number, institutionId: number): Promise<void> => {
        try {
            await this.network.sendApi(`${this.getOdataRoute()}(${contactNoteId})/PersonToInstitutionRelations/$ref`, {
                "@odata.id": `${Network.OdataBaseURL}PersonToInstitution(${personId}, ${institutionId})`,
            });
        } catch (e) {
            this.logger.log(Loglevel.Error, "Unable to link the person to institution relation to the contact note", e);
        }
    };

    public linkInstitutionEntry = async (contactNoteId: number, institutionId: number): Promise<void> => {
        try {
            await this.network.sendApi(`${this.getOdataRoute()}(${contactNoteId})/Institutions/$ref`, {
                "@odata.id": `${Network.OdataBaseURL}Institution(${institutionId})`,
            });
        } catch (e) {
            this.logger.log(Loglevel.Error, "Unable to link the institution to the contact note", e);
        }
    };

    public linkPersonEntry = async (contactNoteId: number, personId: number): Promise<void> => {
        try {
            await this.network.sendApi(`${this.getOdataRoute()}(${contactNoteId})/Persons/$ref`, {
                "@odata.id": `${Network.OdataBaseURL}People(${personId})`,
            });
        } catch (e) {
            this.logger.log(Loglevel.Error, "Unable to link the person to the contact note", e);
        }
    };
}
